import {Injectable} from "@angular/core";
import {
    BusinessUnit,
    OrgBUProjectTeamStructure,
    Project,
    Team,
} from "../../../models/custom-models/org-buproject-team-structure";
import {Constants} from "../../../models/constants";
import * as fuzzysort from "fuzzysort";
import {EntityInfo} from "../../../models/custom-models/entity-info";

@Injectable({
    providedIn: "root",
})
export class OrgBuProjectTeamStructureService {
    checkDefaultsConfig = {
        checkBUDefault: true,
        checkProjectDefault: true,
    };

    constructor() {}

    createOrgBuProjectTeamStructure(
        organizationsData: any,
        isFilterPersonalOrg: boolean = false,
        checkBUDefault: boolean = this.checkDefaultsConfig.checkBUDefault,
        checkProjectDefault: boolean = this.checkDefaultsConfig.checkProjectDefault,
        isSelectableFallBack: boolean = false,
    ) {
        this.checkDefaultsConfig.checkBUDefault = checkBUDefault;
        this.checkDefaultsConfig.checkProjectDefault = checkProjectDefault;
        let orgBuProjectTeamStructure: OrgBUProjectTeamStructure[] = [];
        for (const responseElement of organizationsData) {
            let orgData: OrgBUProjectTeamStructure = {
                name: responseElement.orgName,
                children: [],
                id: responseElement.orgId,
                entityType: Constants.EntityTypesName.ORGANIZATION,
                entityTypeId: Constants.EntityTypes.ORG,
                isSelectable: responseElement.isSelectable ?? isSelectableFallBack,
                orgId: responseElement.orgId,
            } as OrgBUProjectTeamStructure;

            // For some screen names the responseElement.bu is null thus the function is quitting early to ensure it runs even when bu data is not present traverse it on empty array.
            const buList = Array.isArray(responseElement.bu) ? responseElement.bu : [];
            for (const buElement of buList) {
                if (!this.isDefaultBU(buElement.buName)) {
                    let buData = this.createBUData(buElement, orgData.orgId!, isSelectableFallBack);
                    this.processProjects(buElement, buData, orgData, isFilterPersonalOrg, isSelectableFallBack);
                    orgData.children?.push(buData);
                } else {
                    this.processDefaultBU(buElement, orgData, isFilterPersonalOrg, isSelectableFallBack);
                }
            }
            if (
                isFilterPersonalOrg &&
                orgData.id == Constants.EntityTypes.PERSONAL_ORG_ID &&
                orgData?.children?.length == 0
            )
                continue;
            orgBuProjectTeamStructure.push(orgData);
        }
        return orgBuProjectTeamStructure;
    }

    // Method to check if only one selectable team is present and return that team, or return false if more than one
    checkIsOnlyOneSelectableTeamPresent(data: OrgBUProjectTeamStructure[]): OrgBUProjectTeamStructure | false {
        let selectableTeam: OrgBUProjectTeamStructure | null = null;
        for (let org of data) {
            if (!org.children) continue;
            for (let bu of org.children) {
                if (!bu.children) continue;

                for (let project of bu.children) {
                    if (!project.children) continue;

                    for (let team of project.children) {
                        if (team.isSelectable) {
                            if (selectableTeam) {
                                return false; // More than one selectable team found
                            }
                            selectableTeam = team;
                        }
                    }
                }
            }
        }
        return selectableTeam || false; // Return the team if exactly one is found, otherwise false
    }

    findNodeBasedOnEntity(
        entityId: number,
        entityTypeId: number,
        data: OrgBUProjectTeamStructure[],
    ): OrgBUProjectTeamStructure | undefined {
        for (let entity of data) {
            if (entity.id == entityId && entity.entityTypeId == entityTypeId && entity.isSelectable) {
                return entity;
            }
            if (entity.children) {
                let foundEntity = this.findNodeBasedOnEntity(entityId, entityTypeId, entity.children);
                if (foundEntity) {
                    return foundEntity;
                }
            }
        }
        return undefined;
    }

     hasTeamEntity(node: OrgBUProjectTeamStructure): boolean {
        if (!node) return false;

        // Base case: If the current node is a team, return true immediately
        if (node.entityTypeId === Constants.EntityTypes.TEAM) {
            return true;
        }

        // Recursive case: If it has children, check if ANY of the children (or their children) is a team
        if (node.children && node.children.length > 0) {
            return node.children.some((child: any) => this.hasTeamEntity(child));
        }

        return false;
    }

    getFirstSelectableNodeBasedOnEntity(
        entityTypeId: number,
        data: OrgBUProjectTeamStructure[],
    ): OrgBUProjectTeamStructure | null {
        let result: OrgBUProjectTeamStructure | null = null;
        const traverse = (nodes: OrgBUProjectTeamStructure[]) => {
            for (let node of nodes) {
                if (result) return;

                if (node.entityTypeId === entityTypeId && node.isSelectable) {
                    result = node;
                    return;
                }
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            }
        };
        traverse(data);
        return result;
    }

    getFirstSelectableEntityOfOrganization(
        data: OrgBUProjectTeamStructure[],
        orgId: number,
        entityTypeId: number,
    ): OrgBUProjectTeamStructure | null {
        let result: OrgBUProjectTeamStructure | null = null;
        const traverse = (nodes: OrgBUProjectTeamStructure[]) => {
            for (let node of nodes) {
                if (node.orgId === orgId && node.entityTypeId === entityTypeId && node.isSelectable) {
                    result = node;
                    return;
                }
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            }
        };
        traverse(data);
        return result;
    }

    getAllEntitiesFromStructure(
        data: OrgBUProjectTeamStructure[],
        entityTypeId: number,
        applySelectableFilter = false,
    ): OrgBUProjectTeamStructure[] {
        let result: OrgBUProjectTeamStructure[] = [];
        const traverse = (nodes: OrgBUProjectTeamStructure[]) => {
            for (let node of nodes) {
                if (node.entityTypeId === entityTypeId) {
                    if (applySelectableFilter && !node.isSelectable) continue;
                    result.push(node);
                }
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            }
        };
        traverse(data);
        return result;
    }

    matchEntityByCode(
        allEntities: OrgBUProjectTeamStructure[],
        entityCode: string,
    ): {
        entity: OrgBUProjectTeamStructure | null;
        score: number;
    } {
        let maxMatchScore = -Infinity;
        let bestMatchEntity: OrgBUProjectTeamStructure | null = null;

        for (let entity of allEntities) {
            if (entity.teamCode) {
                if (entity.teamCode.toLowerCase() === entityCode.toLowerCase()) {
                    return { entity: entity, score: Infinity }; // Exact match
                }

                const match = fuzzysort.single(entityCode, entity.teamCode);
                if (match && match.score && match.score > maxMatchScore) {
                    maxMatchScore = match.score;
                    bestMatchEntity = entity;
                }
            }
        }

        return { entity: bestMatchEntity, score: maxMatchScore };
    }

    matchEntityByName(
        allEntities: OrgBUProjectTeamStructure[],
        entityName: string,
    ): {
        entity: OrgBUProjectTeamStructure | null;
        score: number;
    } {
        let maxMatchScore = -Infinity;
        let bestMatchEntity: OrgBUProjectTeamStructure | null = null;

        for (let entity of allEntities) {
            const match = fuzzysort.single(entityName, entity.name);
            if (match && match.score && match.score > maxMatchScore) {
                maxMatchScore = match.score;
                bestMatchEntity = entity;
            }
        }

        return { entity: bestMatchEntity, score: maxMatchScore };
    }

    // Implemented dfs approach to traverse through the whole node to get the entity match
    matchEntityByName2(
        allEntities: OrgBUProjectTeamStructure[],
        entityName: string,
    ): {
        entity: OrgBUProjectTeamStructure | null;
        score: number;
    } {
        let maxMatchScore = -Infinity;
        let bestMatchEntity: OrgBUProjectTeamStructure | null = null;

        // --- DFS Traversal Function ---
        function dfs(node: OrgBUProjectTeamStructure) {
            // Check current node
            const match = fuzzysort.single(entityName, node.name);

            if (match && match.score > maxMatchScore) {
                maxMatchScore = match.score;
                bestMatchEntity = node;
            }

            // Go deeper into children
            if (node.children && node.children.length > 0) {
                for (const child of node.children) {
                    dfs(child);
                }
            }
        }

        // --- Start DFS for every root entity ---
        for (const rootEntity of allEntities) {
            dfs(rootEntity);
        }

        return { entity: bestMatchEntity, score: maxMatchScore };
    }



    findBestMatchEntity(
        allEntities: OrgBUProjectTeamStructure[],
        entityCode: string | null,
        entityName: string | null | undefined,
    ): OrgBUProjectTeamStructure | null {
        let entityCodeMatchScore = -Infinity;
        let entityNameMatchScore = -Infinity;
        let entityBasedOnMaxCodeMatch: OrgBUProjectTeamStructure | null = null;
        let entityBasedOnMaxNameMatch: OrgBUProjectTeamStructure | null = null;

        if (entityCode) {
            const codeMatchResult = this.matchEntityByCode(allEntities, entityCode);
            entityBasedOnMaxCodeMatch = codeMatchResult.entity;
            entityCodeMatchScore = codeMatchResult.score;
        }

        if (entityName) {
            const nameMatchResult = this.matchEntityByName2(allEntities, entityName);
            entityBasedOnMaxNameMatch = nameMatchResult.entity;
            entityNameMatchScore = nameMatchResult.score;
        }

        // Compare match scores and return the best match entity
        if (entityBasedOnMaxCodeMatch && entityBasedOnMaxNameMatch) {
            if (entityCodeMatchScore > entityNameMatchScore) {
                return entityBasedOnMaxCodeMatch;
            } else if (entityNameMatchScore > entityCodeMatchScore) {
                return entityBasedOnMaxNameMatch;
            }
        } else if (entityBasedOnMaxCodeMatch) {
            return entityBasedOnMaxCodeMatch;
        } else if (entityBasedOnMaxNameMatch) {
            return entityBasedOnMaxNameMatch;
        }
        return null;
    }

    private isDefaultBU(buName: string) {
        return this.checkDefaultsConfig.checkBUDefault && buName.endsWith("_DFLT");
    }

    private isDefaultProject(projectName: string) {
        return this.checkDefaultsConfig.checkProjectDefault && projectName.endsWith("_DFLT");
    }

    private createBUData(
        buElement: BusinessUnit,
        orgId: number,
        isSelectableFallBack: boolean,
    ): OrgBUProjectTeamStructure {
        return {
            name: buElement.buName,
            children: [],
            id: buElement.buId,
            entityType: Constants.EntityTypesName.BU,
            entityTypeId: Constants.EntityTypes.BU,
            isSelectable: buElement.isSelectable ?? isSelectableFallBack,
            orgId: orgId,
            buId: buElement.buId,
        } as OrgBUProjectTeamStructure;
    }

    private processProjects(
        buElement: BusinessUnit,
        buData: OrgBUProjectTeamStructure,
        orgData: OrgBUProjectTeamStructure,
        isFilterPersonalOrg: boolean,
        isSelectableFallBack: boolean,
    ): void {
        for (const projectElement of buElement?.projects) {
            if (!this.isDefaultProject(projectElement.projectName)) {
                let projectData = this.createProjectData(
                    projectElement,
                    isSelectableFallBack,
                    orgData?.orgId!,
                    buData?.buId!,
                );
                projectElement?.teams.forEach((teamElement: Team) => {
                    if (isFilterPersonalOrg && teamElement.teamId == Constants.EntityTypes.PERSONAL_TEAM_ID) return;
                    projectData.children?.push(
                        this.createTeamData(
                            teamElement,
                            isSelectableFallBack,
                            orgData?.orgId!,
                            buData?.buId!,
                            projectData?.projectId,
                        ),
                    );
                });
                buData.children?.push(projectData);
            } else {
                projectElement?.teams.forEach((teamElement: Team) => {
                    if (isFilterPersonalOrg && teamElement.teamId == Constants.EntityTypes.PERSONAL_TEAM_ID) return;
                    buData.children?.push(
                        this.createTeamData(
                            teamElement,
                            isSelectableFallBack,
                            orgData?.orgId!,
                            buData?.buId!,
                            projectElement.projectId,
                        ),
                    );
                });
            }
        }
    }

    private createTeamData(
        teamElement: Team,
        isSelectableFallBack: boolean,
        orgId: number,
        buId?: number,
        projectId?: number,
    ): OrgBUProjectTeamStructure {
        return {
            name: teamElement.teamName,
            children: [],
            id: teamElement.teamId,
            entityType: Constants.EntityTypesName.TEAM,
            entityTypeId: Constants.EntityTypes.TEAM,
            isSelectable: teamElement.isSelectable ?? isSelectableFallBack,
            orgId: orgId,
            buId: buId,
            projectId: projectId,
            teamId: teamElement.teamId,
            teamCode: teamElement.teamCode,
        } as OrgBUProjectTeamStructure;
    }

    private processDefaultBU(
        buElement: BusinessUnit,
        orgData: OrgBUProjectTeamStructure,
        isFilterPersonalOrg: boolean,
        isSelectableFallBack: boolean,
    ): void {
        for (const projectElement of buElement?.projects) {
            if (!this.isDefaultProject(projectElement.projectName)) {
                let projectData = this.createProjectData(
                    projectElement,
                    isSelectableFallBack,
                    orgData?.orgId!,
                    buElement?.buId,
                );
                projectElement?.teams.forEach((teamElement: Team) => {
                    if (isFilterPersonalOrg && teamElement.teamId == Constants.EntityTypes.PERSONAL_TEAM_ID) return;
                    projectData.children?.push(
                        this.createTeamData(
                            teamElement,
                            isSelectableFallBack,
                            orgData?.orgId!,
                            buElement?.buId!,
                            projectData?.projectId,
                        ),
                    );
                });
                orgData.children?.push(projectData);
            } else {
                projectElement?.teams.forEach((teamElement: Team) => {
                    if (isFilterPersonalOrg && teamElement.teamId == Constants.EntityTypes.PERSONAL_TEAM_ID) return;
                    orgData.children?.push(
                        this.createTeamData(
                            teamElement,
                            isSelectableFallBack,
                            orgData?.orgId!,
                            buElement?.buId!,
                            projectElement.projectId,
                        ),
                    );
                });
            }
        }
    }

    private createProjectData(
        projectElement: Project,
        isSelectableFallBack: boolean,
        orgId: number,
        buId?: number,
    ): OrgBUProjectTeamStructure {
        return {
            name: projectElement.projectName,
            children: [],
            id: projectElement.projectId,
            entityType: Constants.EntityTypesName.PROJECT,
            entityTypeId: Constants.EntityTypes.PROJECT,
            isSelectable: projectElement.isSelectable ?? isSelectableFallBack,
            orgId: orgId,
            buId: buId,
            projectId: projectElement.projectId,
        };
    }

    checkIfNodeSelectable(data: OrgBUProjectTeamStructure[], target: EntityInfo):boolean{
        if(!data || !target) {return false;}

        for(const node of data){
            // check for both entityId and entityTypeId match
            if(node.id === target.entityId && node.entityTypeId === target.entityTypeId){
                return node.isSelectable === true;
            }

            if(node.children && node.children.length > 0){
                const found = this.checkIfNodeSelectable(node.children, target);

                if(found) return true;
            }
        }

        return false;
    }

    // Function to check if preferred Node is selectable of not based on preferred node id
    checkIfPrefNodeSelectable(data: OrgBUProjectTeamStructure[], target: number | null ){
          if(!data || !target) {return false;}

        for(const node of data){
               if(node.id === target){
                return node.isSelectable === true;
            }

            if(node.children && node.children.length > 0){
                const found = this.checkIfPrefNodeSelectable(node.children, target);

                if(found) return true;
            }

        }
        return false

    }
}
